const SAFE_BASE_ORIGIN = 'https://cms.storyteller.invalid';
const MAXIMUM_REDIRECT_LENGTH = 2048;
const GUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
const WORKSPACE_KEY_PATTERN = /^[A-Za-z0-9_-]{32,128}$/;
const AUTHENTICATION_PATHS = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/password-reset-confirmation',
];

const containsControlCharacter = (value: string) =>
  Array.from(value).some(character => {
    const code = character.charCodeAt(0);
    return code <= 31 || code === 127;
  });

const parseLocalPath = (value: unknown): URL | undefined => {
  if (
    typeof value !== 'string' ||
    value.length < 1 ||
    value.length > MAXIMUM_REDIRECT_LENGTH ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('\\') ||
    containsControlCharacter(value)
  ) {
    return undefined;
  }

  try {
    const parsed = new URL(value, SAFE_BASE_ORIGIN);
    return parsed.origin === SAFE_BASE_ORIGIN &&
      !parsed.username &&
      !parsed.password
      ? parsed
      : undefined;
  } catch {
    return undefined;
  }
};

const isNonEmptyGuid = (value: string) =>
  GUID_PATTERN.test(value) && value.toLowerCase() !== EMPTY_GUID;

export const isSafePremiereReturnPath = (value: unknown): value is string => {
  const parsed = parseLocalPath(value);
  if (!parsed || parsed.hash) {
    return false;
  }

  if (parsed.pathname === '/premiere/authorize') {
    return isNonEmptyGuid(parsed.searchParams.get('loginRequestId') || '');
  }

  const workspaceMatch = parsed.pathname.match(
    /^\/premiere\/workspaces\/([^/]+)$/
  );
  if (workspaceMatch) {
    return WORKSPACE_KEY_PATTERN.test(workspaceMatch[1]);
  }

  const assetMatch = parsed.pathname.match(
    /^\/premiere\/workspaces\/([^/]+)\/mediaLibraries\/externalAssets\/([^/]+)$/
  );
  return Boolean(
    assetMatch &&
      WORKSPACE_KEY_PATTERN.test(assetMatch[1]) &&
      isNonEmptyGuid(assetMatch[2])
  );
};

export const isSafeLocalLoginRedirect = (value: unknown): value is string => {
  const parsed = parseLocalPath(value);
  if (!parsed || parsed.hash) {
    return false;
  }

  if (parsed.pathname.startsWith('/premiere')) {
    return isSafePremiereReturnPath(value);
  }

  return !AUTHENTICATION_PATHS.some(path => parsed.pathname.startsWith(path));
};

export const readLoginRedirect = (search: string): string | undefined => {
  if (search.length > MAXIMUM_REDIRECT_LENGTH * 2) {
    return undefined;
  }

  const params = new URLSearchParams(search);
  const candidates = [
    ...params.getAll('redirectUrl'),
    ...params.getAll('returnUrl'),
  ];
  return candidates.length === 1 && isSafeLocalLoginRedirect(candidates[0])
    ? candidates[0]
    : undefined;
};

export const buildLoginPath = (returnPath?: string) =>
  returnPath && isSafeLocalLoginRedirect(returnPath)
    ? `/login?redirectUrl=${encodeURIComponent(returnPath)}`
    : '/login';

export const getCurrentLoginReturnPath = (
  pathname: string,
  search: string
): string | undefined => {
  const premiereCandidate = `${pathname}${search}`;
  if (pathname.startsWith('/premiere')) {
    return isSafePremiereReturnPath(premiereCandidate)
      ? premiereCandidate
      : undefined;
  }

  return isSafeLocalLoginRedirect(pathname) ? pathname : undefined;
};

export const createPremiereOidcState = (returnPath?: string) =>
  isSafePremiereReturnPath(returnPath) ? { returnPath } : undefined;

export const readPremiereOidcReturnPath = (
  state: unknown
): string | undefined => {
  if (!state || typeof state !== 'object' || Array.isArray(state)) {
    return undefined;
  }

  const record = state as Record<string, unknown>;
  return isSafePremiereReturnPath(record.returnPath)
    ? record.returnPath
    : undefined;
};
